// FLOW-03b (Task 3.2; PLAN P10 e P11) — os tipos de gatilho como a tela os mostra: o ícone, a família
// de cor, o título e a linha da gaveta, os sinônimos da busca e o resumo de uma linha do nó de topo.
// Um flow começa de um jeito só (P1): `gatilho: {agente: {}}`, `{agenda: {...}}` ou `{webhook: {...}}`,
// ou o `gatilhos:` de antes, com um item e o `id` dele (P2).
//
// As cores são as famílias do `tiposDePasso.js` que não são cor de estado: o Agente em iris (a família
// `ia`, a do passo `agent`), a Agenda em violeta (a do `fluxo`, a do `wait`) e o Webhook em laranja (a
// da `integracao`, a do passo `http`, que também fala HTTP).
//
// Os sinônimos são o vocabulário de quem procura, como os `termos` de `settings/powerAgents/busca.js`:
// quem quer a agenda digita "cron" ou "horário", e quem quer o webhook, "url" ou "api". Valem nas duas
// línguas, ao lado do título e da linha traduzidos.
import { ICONES_DOS_GATILHOS } from '../../../powerflow/gatilhosNaTela';
import { normalizar } from '../../powerAgents/busca';

const ehObjeto = valor =>
  !!valor && typeof valor === 'object' && !Array.isArray(valor);

// Na ordem da gaveta.
export const TIPOS_DE_GATILHO = {
  agente: {
    familia: 'ia',
    icone: ICONES_DOS_GATILHOS.agente,
    sinonimos: ['tool', 'IA'],
  },
  agenda: {
    familia: 'fluxo',
    icone: ICONES_DOS_GATILHOS.agenda,
    sinonimos: ['cron', 'horário', 'a cada'],
  },
  webhook: {
    familia: 'integracao',
    icone: ICONES_DOS_GATILHOS.webhook,
    sinonimos: ['url', 'http', 'api'],
  },
};

// Os eventos do OS ainda não disparam flow: a gaveta os mostra "Em breve", desligados.
export const EVENTOS_EM_BREVE = {
  mensagem_nova: { icone: 'i-lucide-message-square-plus' },
  conversa_resolvida: { icone: 'i-lucide-circle-check' },
  contato_criado: { icone: 'i-lucide-user-plus' },
};

// O título e a linha de cada item da gaveta, com a chave inteira em cada `t` (a regra
// `no-dynamic-keys`). O título do tipo é o das telas da aba PowerFlow (`TRIGGERS.KINDS`).
export const textosDosGatilhos = t => ({
  agente: {
    nome: t('POWER_TOOLS.POWERFLOW.TRIGGERS.KINDS.AGENT'),
    desc: t('POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.KINDS.AGENT'),
  },
  agenda: {
    nome: t('POWER_TOOLS.POWERFLOW.TRIGGERS.KINDS.SCHEDULE'),
    desc: t('POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.KINDS.SCHEDULE'),
  },
  webhook: {
    nome: t('POWER_TOOLS.POWERFLOW.TRIGGERS.KINDS.WEBHOOK'),
    desc: t('POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.KINDS.WEBHOOK'),
  },
  mensagem_nova: {
    nome: t('POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.EVENTS.NEW_MESSAGE.NAME'),
    desc: t('POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.EVENTS.NEW_MESSAGE.DESC'),
  },
  conversa_resolvida: {
    nome: t(
      'POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.EVENTS.CONVERSATION_RESOLVED.NAME'
    ),
    desc: t(
      'POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.EVENTS.CONVERSATION_RESOLVED.DESC'
    ),
  },
  contato_criado: {
    nome: t(
      'POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.EVENTS.CONTACT_CREATED.NAME'
    ),
    desc: t(
      'POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.EVENTS.CONTACT_CREATED.DESC'
    ),
  },
});

// FLOW-03b (Task 3.5; PLAN P11): o título do gatilho no nó de topo e na caixa dele (mockup, telas 3 e
// 4). O Agente é "Chamado pelo agente": no canvas, o nome do tipo sozinho não diz quem começa o flow.
export const tituloDoGatilho = (t, tipo) =>
  ({
    agente: t('POWER_TOOLS.FLOW_EDITOR.CANVAS.NODE.TRIGGER_AGENT'),
    agenda: t('POWER_TOOLS.POWERFLOW.TRIGGERS.KINDS.SCHEDULE'),
    webhook: t('POWER_TOOLS.POWERFLOW.TRIGGERS.KINDS.WEBHOOK'),
  })[tipo];

// A busca da gaveta: o que se digita casa com o começo de uma palavra do título, da linha, da chave do
// YAML ou de um sinônimo, sem diferença de maiúscula nem de acento (o `normalizar` da busca do agente).
// Pelo começo da palavra, e não em qualquer ponto dela: o termo curto acharia demais, e "IA" acharia o
// "Contato criado".
const emPalavras = texto => ` ${normalizar(texto).replace(/[^a-z0-9]+/g, ' ')}`;

export function casaComABusca(busca, chave, textos) {
  const termo = emPalavras(busca).trim();
  if (!termo) return true;
  const { nome, desc } = textos[chave];
  const sinonimos = TIPOS_DE_GATILHO[chave]?.sinonimos || [];
  return [chave, nome, desc, ...sinonimos].some(texto =>
    emPalavras(texto).includes(` ${termo}`)
  );
}

// O gatilho do flow já lido (o objeto do YAML): `{tipo, forma, id, config}`. A forma é `singular`, a do
// `gatilho:`, cujo id é o próprio tipo (P2), ou `lista`, a do `gatilhos:` de antes, com o `id` do item.
// Sem gatilho, nulo: quem chama decide entre o nó vazio e o Agente (o flow já publicado sem gatilho e
// a ferramenta HTTP são de Agente).
export function tipoDoGatilho(documento) {
  const unico = documento?.gatilho;
  const doUnico =
    ehObjeto(unico) &&
    Object.keys(TIPOS_DE_GATILHO).find(tipo => tipo in unico);
  if (doUnico) {
    return {
      tipo: doUnico,
      forma: 'singular',
      id: doUnico,
      config: unico[doUnico] || {},
    };
  }
  const [item] = Array.isArray(documento?.gatilhos) ? documento.gatilhos : [];
  const daLista = ['agenda', 'webhook'].find(tipo => ehObjeto(item?.[tipo]));
  return daLista
    ? { tipo: daLista, forma: 'lista', id: item.id, config: item[daLista] }
    : null;
}

// O resumo de uma linha, embaixo do título do nó de topo (P11): no Agente, quantos parâmetros o flow
// pede; na Agenda, a frase da regra que a caixa mostra (a da prévia, que o PowerTools calcula e o
// `gatilhosDoEditor` guarda em `previas`); no Webhook, o método e quando quem chama recebe a resposta.
// Sem a prévia (a caminho, ou a regra torta), a Agenda fica sem resumo: o erro é o do compilador.
export function resumoDoGatilho(
  t,
  { tipo, config = {}, parametros = 0, frase = null }
) {
  if (tipo === 'agente') {
    return parametros
      ? t(
          'POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.SUMMARY.PARAMS',
          { n: parametros },
          parametros
        )
      : t('POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.SUMMARY.NO_PARAMS');
  }
  if (tipo === 'webhook') {
    const metodo = config?.metodo || 'POST';
    return config?.resposta === 'respond'
      ? t('POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.SUMMARY.WEBHOOK_WAIT', {
          metodo,
        })
      : t('POWER_TOOLS.FLOW_EDITOR.TRIGGER_DRAWER.SUMMARY.WEBHOOK_NOW', {
          metodo,
        });
  }
  // A frase da prévia vem em minúscula, para caber no meio da linha da lista de Flows ("Agenda · a
  // cada 15 min"). No nó, ela abre a linha, como na tela 3 dos mockups ("A cada 1 hora").
  return frase ? frase.charAt(0).toUpperCase() + frase.slice(1) : '';
}
